import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import { Bot, User, Clock, Trash2, Link2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { TYPE_META, PRIORITY_META, STATUS_META, BOARD_COLUMNS, actorLabel } from "./taskConstants";
import { MoveReasonDialog } from "./MoveReasonDialog";
import { ConfirmDialog } from "../shared/ConfirmDialog";

export function TaskDetailPanel({ taskId, open, onOpenChange, onChanged }) {
  const { token, isAdmin } = useAuth();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [events, setEvents] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [t, c, e] = await Promise.all([
        api.getTask(token, taskId), api.getTaskComments(token, taskId), api.getTaskEvents(token, taskId),
      ]);
      setTask(t); setComments(c); setEvents(e);
    } catch (e) {
      toast.error("No se pudo cargar la tarea");
    } finally {
      setLoading(false);
    }
  }, [taskId, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const move = async (to) => {
    if (to === "in_review" || to === "blocked") { setPendingMove(to); return; }
    try {
      await api.moveTask(token, taskId, { to });
      toast.success(`Movida a "${STATUS_META[to].label}"`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo mover la tarea");
    }
  };

  const confirmMove = async (text) => {
    const extra = pendingMove === "in_review" ? { summary: text } : { reason: text };
    try {
      await api.moveTask(token, taskId, { to: pendingMove, ...extra });
      toast.success(`Movida a "${STATUS_META[pendingMove].label}"`);
      setPendingMove(null); load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo mover la tarea");
      setPendingMove(null);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.addTaskComment(token, taskId, { kind: "comment", body: newComment.trim() });
      setNewComment(""); load();
    } catch (e) {
      toast.error("No se pudo comentar");
    }
  };

  const doDelete = async () => {
    try {
      await api.deleteTask(token, taskId);
      toast.success("Tarea eliminada");
      setConfirmDelete(false); onOpenChange(false); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  if (!task && !loading) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="task-detail-panel">
        {task && (
          <>
            <SheetHeader>
              <SheetTitle className="pr-6">{task.title}</SheetTitle>
            </SheetHeader>
            <div className="space-y-4 mt-4">
              <div className="flex flex-wrap gap-1.5 items-center">
                <Badge className={`border-0 ${TYPE_META[task.type]?.color}`}>{TYPE_META[task.type]?.label}</Badge>
                <Badge className={`border-0 ${PRIORITY_META[task.priority]?.color}`}>{task.priority}</Badge>
                <Badge variant="outline">{STATUS_META[task.status]?.label}</Badge>
                {task.initiative_id && <Badge variant="outline" className="gap-1"><Link2 className="w-3 h-3" />Vinculada a iniciativa</Badge>}
              </div>

              {task.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.description}</p>}

              {task.status === "blocked" && task.blocked_reason && (
                <div className="text-sm bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md p-2 text-red-700 dark:text-red-300" data-testid="task-blocked-reason">
                  <strong>Bloqueada:</strong> {task.blocked_reason}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Solicitante</p>
                  <p className="flex items-center gap-1">{task.requester?.kind === "agent" ? <Bot className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}{actorLabel(task.requester)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Responsable</p>
                  <p className="flex items-center gap-1">{task.assignee ? (<>{task.assignee.kind === "agent" ? <Bot className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}{actorLabel(task.assignee)}</>) : <span className="italic text-muted-foreground">Sin asignar</span>}</p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Mover a</p>
                <div className="flex flex-wrap gap-1.5">
                  {BOARD_COLUMNS.filter((s) => s !== task.status).map((s) => (
                    <Button key={s} size="sm" variant="outline" onClick={() => move(s)} data-testid={`task-move-to-${s}`}>
                      {STATUS_META[s].label}
                    </Button>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Comentarios</p>
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="task-comments-list">
                  {comments.map((c) => (
                    <div key={c.id} className="text-sm bg-muted/50 rounded-md p-2">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        {c.author?.kind === "agent" ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {actorLabel(c.author)} · {c.kind}
                      </p>
                      <p className="whitespace-pre-wrap">{c.body}</p>
                    </div>
                  ))}
                  {comments.length === 0 && <p className="text-xs text-muted-foreground italic">Sin comentarios todavía</p>}
                </div>
                <div className="flex gap-2 mt-2">
                  <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Escribe un comentario..." rows={2} data-testid="task-new-comment-input" />
                  <Button size="sm" onClick={submitComment} data-testid="task-new-comment-submit">Enviar</Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><Clock className="w-3 h-3" />Historial</p>
                <div className="space-y-1 max-h-40 overflow-y-auto text-xs text-muted-foreground" data-testid="task-events-list">
                  {events.map((e) => (
                    <p key={e.id}>
                      {new Date(e.at).toLocaleString("es-ES")} · {actorLabel(e.actor)} · {e.action}
                      {e.field && ` (${e.field}: ${e.from ?? "—"} → ${e.to ?? "—"})`}
                    </p>
                  ))}
                </div>
              </div>

              {isAdmin && (
                <>
                  <Separator />
                  <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} className="gap-1" data-testid="task-delete-button">
                    <Trash2 className="w-3.5 h-3.5" />Eliminar tarea
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </SheetContent>

      <MoveReasonDialog open={!!pendingMove} kind={pendingMove} onCancel={() => setPendingMove(null)} onConfirm={confirmMove} />
      <ConfirmDialog
        open={confirmDelete} onOpenChange={setConfirmDelete} title="¿Eliminar esta tarea?"
        description="Esta acción no se puede deshacer." confirmLabel="Eliminar" onConfirm={doDelete} testId="task-delete-confirm"
      />
    </Sheet>
  );
}
